'use client';

import { useState, useEffect, useRef } from "react";

/**
 * "Dictate less" — DXR's dictation intelligence. The figure (left) shows a
 * few dictated abnormals → a full streaming, auto-scrolling generated report.
 * Value points on the right. Styled to match the personalization spotlight.
 */

const IconMic = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="2" width="6" height="11" rx="3" /><path d="M5 10a7 7 0 0 0 14 0M12 17v4" />
  </svg>
);
const IconSpell = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 17l4-10 4 10M5 14h6" /><path d="M14 13.5l2 2 4-4.5" />
  </svg>
);
const IconGrammar = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l1.8 4.6L18.5 9l-4.7 1.4L12 15l-1.8-4.6L5.5 9l4.7-1.4z" /><path d="M18 16l.9 2.3L21 19l-2.1.7L18 22l-.9-2.3L15 19l2.1-.7z" />
  </svg>
);
const IconSections = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="4" rx="1" /><rect x="3" y="10" width="11" height="4" rx="1" /><rect x="3" y="16" width="18" height="4" rx="1" />
  </svg>
);

const POINTS = [
  {
    Icon: IconMic,
    title: "Only dictate the abnormals",
    body: "Skip the normals — DXR fills every normal section, so a full report comes from a handful of words.",
  },
  {
    Icon: IconSpell,
    title: "Spelling & abbreviations, handled",
    body: "Misspoken, misspelled, or abbreviated — DXR knows radiology shorthand and writes it correctly.",
  },
  {
    Icon: IconGrammar,
    title: "Forget perfect grammar",
    body: "No punctuation, conjunctions, or full sentences needed — DXR turns rough dictation into clean prose.",
  },
  {
    Icon: IconSections,
    title: "Say it in any order",
    body: "Jump between findings however they come to you — DXR files each one under the right section automatically.",
  },
];

// Generated CT CHEST report — lbl = white/bold/upper label, b = blue body,
// hl = amber abnormal finding, title = report title.
const REPORT = [
  { type: 'title', segs: [{ c: 'title', t: 'CT CHEST' }] },
  { type: 'sec', segs: [{ c: 'lbl', t: 'Technique:' }, { c: 'b', t: 'Axial CT of the chest acquired with and without IV contrast. Dose-reduction technique applied (automated exposure control, size-adjusted mA/kV, iterative reconstruction).' }] },
  { type: 'sec', segs: [{ c: 'lbl', t: 'Clinical History:' }, { c: 'b', t: 'None.' }] },
  { type: 'sec', segs: [{ c: 'lbl', t: 'Comparison:' }, { c: 'b', t: 'None.' }] },
  { type: 'sec', segs: [{ c: 'lbl', t: 'Findings:' }] },
  { type: 'line', segs: [{ c: 'lbl', t: 'Lungs:' }, { c: 'hl', t: 'There is a nodule in the right lung.' }, { c: 'b', t: 'The remaining lung parenchyma is clear.' }] },
  { type: 'line', segs: [{ c: 'lbl', t: 'Pleural Spaces:' }, { c: 'hl', t: 'Mild right pleural effusion.' }, { c: 'b', t: 'No pneumothorax.' }] },
  { type: 'line', segs: [{ c: 'lbl', t: 'Heart and Mediastinum:' }, { c: 'b', t: 'No cardiomegaly. No significant pericardial effusion. Great vessels normal in caliber.' }] },
  { type: 'line', segs: [{ c: 'lbl', t: 'Lymph Nodes:' }, { c: 'hl', t: 'A few borderline mediastinal nodes.' }, { c: 'b', t: 'No hilar or axillary lymphadenopathy.' }] },
  { type: 'line', segs: [{ c: 'lbl', t: 'Bones and Soft Tissues:' }, { c: 'b', t: 'No acute osseous abnormality. Soft tissues within normal limits.' }] },
  { type: 'sec', segs: [{ c: 'lbl', t: 'Impression:' }] },
  { type: 'line', segs: [{ c: 'b', t: '1.' }, { c: 'hl', t: 'Right lung nodule.' }] },
  { type: 'line', segs: [{ c: 'b', t: '2.' }, { c: 'hl', t: 'Mild right pleural effusion.' }] },
  { type: 'line', segs: [{ c: 'b', t: '3.' }, { c: 'hl', t: 'Borderline mediastinal lymph nodes.' }] },
];

const REPORT_WORDS = REPORT.flatMap((line, li) =>
  line.segs.flatMap((seg) => seg.t.split(' ').map((w) => ({ li, c: seg.c, w })))
);
const WORD_MS = 55;       // streaming pace
const REPLAY_MS = 2600;   // pause before looping
const SEG_CLASS = { lbl: 'lbl', b: 'b', hl: 'hl', title: '' };

function StreamingReport() {
  const [n, setN] = useState(0);
  const [active, setActive] = useState(false); // on-screen & motion-allowed
  const scrollRef = useRef(null);

  // Only stream while on-screen; honor reduced-motion by showing the full report.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const reduce = typeof window !== "undefined" && window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setN(REPORT_WORDS.length); return; }
    const io = new IntersectionObserver(
      (entries) => setActive(entries[0].isIntersecting),
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Stream word-by-word, then pause and loop (paused while off-screen).
  useEffect(() => {
    if (!active) return;
    const done = n >= REPORT_WORDS.length;
    const t = setTimeout(() => setN(done ? 0 : n + 1), done ? REPLAY_MS : WORD_MS);
    return () => clearTimeout(t);
  }, [n, active]);

  // Auto-scroll to keep the latest streamed line in view.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = n === 0 ? 0 : el.scrollHeight;
  }, [n]);

  const streaming = n < REPORT_WORDS.length;
  const lastLi = n > 0 ? REPORT_WORDS[n - 1].li : -1;
  const byLine = new Map();
  for (let i = 0; i < n; i++) {
    const tok = REPORT_WORDS[i];
    if (!byLine.has(tok.li)) byLine.set(tok.li, []);
    byLine.get(tok.li).push(tok);
  }

  return (
    <div className="dl-rep-body" ref={scrollRef} suppressHydrationWarning>
      {REPORT.map((line, li) => {
        const words = byLine.get(li);
        if (!words) return null;
        const inner = (
          <>
            {words.map((tok, j) => (
              <span key={j} className={`dl-word ${SEG_CLASS[tok.c]}`.trim()}>{tok.w} </span>
            ))}
            {streaming && li === lastLi && <span className="dl-caret" />}
          </>
        );
        if (line.type === 'title') return <div key={li} className="dl-rep-title">{inner}</div>;
        if (line.type === 'sec') return <p key={li} className="dl-rep-sec">{inner}</p>;
        return <p key={li} className="dl-rep-line">{inner}</p>;
      })}
    </div>
  );
}

export default function DxrDictateLess() {
  return (
    <section className="dl">
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="dl-grid">
        {/* Visual: a few dictated abnormals → a full streaming report */}
        <div className="dl-viz" aria-hidden="true">
          <span className="dl-brand">NATOE <b>DXR</b></span>
          <div className="dl-card dl-say">
            <span className="dl-card-label">You dictate</span>
            <p>
              <span className="dl-mic"><IconMic /></span>
              <span>“<span className="dl-typo">Nodul</span> right lung Mild right <span className="dl-typo">plural</span> <span className="dl-typo">effussion</span> Few borderline <span className="dl-typo">medistinal</span> nodes”</span>
            </p>
          </div>
          <div className="dl-arrow">
            <span>DXR writes the rest</span>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#2997FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 4v16m0 0l-6-6m6 6l6-6" /></svg>
          </div>
          <div className="dl-card dl-rep">
            <span className="dl-card-label">Generated report</span>
            <StreamingReport />
          </div>
        </div>

        {/* Text */}
        <div className="dl-text">
          <span className="dl-eyebrow">Reporting reimagined</span>
          <h2 className="dl-h2">Say less. Get the whole report.</h2>
          <p className="dl-sub">DXR understands radiology — so you only say what matters, however you say it.</p>
          <div className="dl-points">
            {POINTS.map((p) => (
              <div key={p.title} className="dl-point">
                <span className="dl-ico"><p.Icon /></span>
                <div>
                  <h3>{p.title}</h3>
                  <p>{p.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const styles = `
.dl { width: 90vw; margin: 0 auto; padding: 5rem 0; font-family: 'Inter', sans-serif; }
.dl-grid { display: grid; grid-template-columns: 0.95fr 1.05fr; gap: 56px; align-items: stretch; }

/* Text side */
.dl-text { display: flex; flex-direction: column; justify-content: center; }
.dl-eyebrow { display: inline-block; font-size: 13px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; background: linear-gradient(134deg, #2997FF, #00109D); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; margin-bottom: 14px; }
.dl-h2 { font-size: clamp(28px, 3.6vw, 44px); font-weight: 700; color: #fff; letter-spacing: -0.02em; margin: 0 0 12px; }
.dl-sub { font-family: 'DM Sans', sans-serif; font-size: 18px; line-height: 1.6; color: rgba(164,164,164,1); margin: 0 0 28px; }
.dl-points { display: flex; flex-direction: column; gap: 20px; }
.dl-point { display: flex; gap: 14px; }
.dl-ico { flex: none; width: 40px; height: 40px; border-radius: 11px; background: linear-gradient(135deg, rgba(41,151,255,0.16), rgba(0,16,157,0.16)); border: 1px solid rgba(41,151,255,0.25); color: #2997FF; display: flex; align-items: center; justify-content: center; }
.dl-point h3 { font-size: 17px; font-weight: 600; color: #fff; margin: 2px 0 4px; }
.dl-point p { font-family: 'DM Sans', sans-serif; font-size: 14.5px; line-height: 1.6; color: rgba(255,255,255,0.65); margin: 0; }

/* Visual side */
.dl-viz { position: relative; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 18px; padding: 28px; display: flex; flex-direction: column; justify-content: center; }
.dl-brand { position: absolute; top: 16px; right: 20px; font-size: 11px; font-weight: 700; letter-spacing: 0.14em; color: rgba(255,255,255,0.5); }
.dl-brand b { color: #2997FF; }
.dl-card { background: #0b0e14; border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; padding: 16px 18px; }
.dl-card-label { display: block; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: rgba(255,255,255,0.45); margin-bottom: 9px; }
.dl-say p { margin: 0; font-size: 15px; line-height: 1.5; color: #fff; display: flex; align-items: flex-start; gap: 9px; }
.dl-mic { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 50%; background: #2997FF; color: #fff; flex: none; margin-top: 1px; }
.dl-typo { color: #FF8585; text-decoration: underline dotted #FF8585; text-decoration-skip-ink: none; text-underline-offset: 3px; text-decoration-thickness: 1.5px; }
.dl-arrow { display: flex; flex-direction: column; align-items: center; gap: 3px; padding: 12px 0; }
.dl-arrow span { font-size: 12px; font-weight: 600; color: #2997FF; }
.dl-rep { display: flex; flex-direction: column; }
.dl-rep-body { height: 210px; overflow-y: auto; scrollbar-width: none; }
.dl-rep-body::-webkit-scrollbar { display: none; }
.dl-word { display: inline; }
.dl-caret { display: inline-block; width: 2px; height: 1em; background: #2997FF; margin-left: 1px; vertical-align: text-bottom; animation: dlBlink 1s step-end infinite; }
@keyframes dlBlink { 50% { opacity: 0; } }
.dl-rep-title { text-align: center; font-weight: 700; text-decoration: underline; color: #fff; font-size: 13px; letter-spacing: 0.04em; margin: 0 0 8px; }
.dl-rep-sec { margin: 5px 0 1px; font-size: 12.5px; line-height: 1.5; }
.dl-rep-line { margin: 2px 0 2px 6px; font-size: 12.5px; line-height: 1.5; }
.dl-rep-body .lbl { color: #fff; font-weight: 700; text-transform: uppercase; }
.dl-rep-body .b { color: #00D6FC; }
.dl-rep-body .hl { color: #FFCC75; font-weight: 600; }

@media (max-width: 860px) {
  .dl-grid { grid-template-columns: 1fr; gap: 32px; }
  .dl-viz { order: 2; }
  .dl-rep-body { height: 220px; flex: none; }
}
`;
